import { TradesResponse } from '../types';

// 🎨 규격 V2(08-23) — 카드 상자 대신 헤어라인 구획, 표는 선으로만. 손익 색은 상승=웜(dc.up)/하락=쿨(dc.down).
const KICKER = 'text-[10.5px] font-[650] tracking-[.12em] text-dc-ink3';
const TH = 'px-2 py-2 text-dc-ink3 text-[10.5px] font-[650] tracking-[.12em] select-none whitespace-nowrap';
const KPI_DIV = 'border-l border-dc-hair pl-5';

// trades = 부모(내 현황)가 받아서 넘겨줌(중복 fetch 방지). unrealizedPnl = 보유 중 평가손익.
// 부모가 'count>0일 때만' 렌더하므로 여기선 빈 상태 처리 불필요(빈 칸 낭비 방지).
export default function RealizedTrades({ trades, unrealizedPnl = 0 }: { trades: TradesResponse; unrealizedPnl?: number }) {
  const data = trades;
  if (!data || data.summary.count === 0) return null;
  const s = data.summary;
  const totalUp = s.total_realized > 0;
  const won = (v: number) => v.toLocaleString('ko-KR');
  const sign = (v: number) => (v > 0 ? '+' : '');
  const pnlClass = (v: number) => (v > 0 ? 'text-dc-up' : v < 0 ? 'text-dc-down' : 'text-dc-ink2');
  const combined = s.total_realized + unrealizedPnl;   // 실현 + 평가 = 총 성과

  return (
    <div className="border-t border-dc-hair pt-4">
      <p className={`${KICKER} mb-2.5`}>실현손익 (매도 기록)</p>

      {/* 통합 성과 — 실현(판 것 누적) + 평가(보유 중) = 합계 */}
      <div className="mb-4 rounded-lg border border-dc-line1 bg-dc-bg1/60 px-3 py-2.5">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className={KICKER}>통합 성과</span>
          <span className="text-[11.5px]" title="지금까지 매도해서 확정한 손익">
            <span className="text-dc-ink3">실현 </span>
            <b className={pnlClass(s.total_realized)}>{sign(s.total_realized)}{won(s.total_realized)}</b>
          </span>
          <span className="text-dc-ink3 text-[11.5px]">+</span>
          <span className="text-[11.5px]" title="현재 보유 중인 종목의 평가손익(아직 안 판 것)">
            <span className="text-dc-ink3">평가 </span>
            <b className={pnlClass(unrealizedPnl)}>{sign(unrealizedPnl)}{won(unrealizedPnl)}</b>
          </span>
          <span className="text-dc-ink3 text-[11.5px]">=</span>
          <span className="text-[13px] ml-auto" title="실현 + 평가 = 누적 총 성과">
            <span className="text-dc-ink2">합계 </span>
            <b className={`font-bold ${pnlClass(combined)}`}>{sign(combined)}{won(combined)}</b>
          </span>
        </div>
        <p className="text-dc-ink3/80 text-[10.5px] mt-1.5">실현=판 것 누적 · 평가=보유 중 평가손익(원)</p>
      </div>

      {/* 통계 — 상자 대신 선으로 나눈 구획 */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-4 mb-4">
        <div className="flex flex-col gap-1">
          <p className={KICKER}>총 실현손익</p>
          <p className={`text-[17.5px] sm:text-[24px] font-[750] tabular-nums leading-none whitespace-nowrap ${totalUp ? 'text-dc-up' : s.total_realized < 0 ? 'text-dc-down' : 'text-dc-ink2'}`}>
            {totalUp ? '+' : ''}{won(s.total_realized)}
          </p>
        </div>
        <div className={`flex flex-col gap-1 ${KPI_DIV}`}>
          <p className={KICKER}>승률</p>
          <p className="text-[17.5px] sm:text-[24px] font-[750] tabular-nums leading-none whitespace-nowrap text-dc-ink1">
            {s.win_rate}<span className="text-[13px] opacity-85">%</span>
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <p className={KICKER}>평균 수익률</p>
          <p className={`text-[17.5px] sm:text-[24px] font-[750] tabular-nums leading-none whitespace-nowrap ${s.avg_pnl_pct >= 0 ? 'text-dc-up' : 'text-dc-down'}`}>
            {s.avg_pnl_pct >= 0 ? '+' : ''}{s.avg_pnl_pct}<span className="text-[13px] opacity-85">%</span>
          </p>
        </div>
        <div className={`flex flex-col gap-1 ${KPI_DIV}`}>
          <p className={KICKER}>거래 수</p>
          <p className="text-[17.5px] sm:text-[24px] font-[750] tabular-nums leading-none whitespace-nowrap text-dc-ink1">
            {s.count}<span className="text-[13px] opacity-85">건</span>
          </p>
        </div>
      </div>

      {/* 내역 */}
      <div className="overflow-x-auto">
        <table className="w-full text-left text-[11.5px]">
          <thead>
            <tr className="border-b border-dc-hair">
              <th className={TH}>날짜</th>
              <th className={TH}>종목</th>
              <th className={`${TH} text-right`}>평단→매도</th>
              <th className={`${TH} text-right`}>수량</th>
              <th className={`${TH} text-right`}>실현손익</th>
            </tr>
          </thead>
          <tbody>
            {data.trades.slice(0, 20).map((t, i) => {
              const up = t.realized_pnl > 0;
              const c = up ? 'text-dc-up' : t.realized_pnl < 0 ? 'text-dc-down' : 'text-dc-ink2';
              return (
                <tr key={i} className="border-b border-dc-hair/80 hover:bg-white/[0.015] transition-colors">
                  <td className="px-2 py-2 text-dc-ink3 tabular-nums">{t.date.slice(5)}</td>
                  <td className="px-2 py-2 text-dc-ink1 font-medium">{t.name}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-dc-ink2">
                    {t.avg_price.toLocaleString('ko-KR')}→{t.sell_price.toLocaleString('ko-KR')}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums text-dc-ink2">{t.quantity}</td>
                  <td className={`px-2 py-2 text-right tabular-nums font-bold ${c}`}>
                    {up ? '+' : ''}{t.realized_pnl.toLocaleString('ko-KR')}
                    <span className="block text-[10.5px] font-normal">{up ? '+' : ''}{t.pnl_pct}%</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
